import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Step1Organization } from '@/components/Onboarding/Step1Organization';
import { Step2Templates } from '@/components/Onboarding/Step2Templates';
import { Step3Buildings, BuildingData } from '@/components/Onboarding/Step3Buildings';
import { Step4CSVImport, ClientData } from '@/components/Onboarding/Step4CSVImport';
import { Step5Review } from '@/components/Onboarding/Step5Review';
import { ValidationError } from '@/components/Onboarding/ValidationErrors';
import { ImportProgress } from '@/components/Onboarding/ImportProgress';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingState {
  currentStep: number;
  organization: {
    name: string;
    logoFile: File | null;
    logoPreview: string;
    adminEmail: string;
    phone: string;
    address: string;
    description: string;
  };
  templates: {
    contractFile: File | null;
    billFile: File | null;
    useDefaults: { contract: boolean; bill: boolean };
  };
  buildings: BuildingData[];
  clients: {
    csvFile: File | null;
    parsedData: ClientData[];
    validationErrors: ValidationError[];
    options: {
      sendWelcomeEmails: boolean;
      autoGenerateClientNumbers: boolean;
      createAccountsImmediately: boolean;
    };
  };
}

const OnboardingSlides: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [importProgress, setImportProgress] = useState({
    visible: false,
    stage: '',
    percent: 0,
    estimatedTime: 0
  });

  // Onboarding is now publicly accessible - no auth check needed

  const [state, setState] = useState<OnboardingState>({
    currentStep: 1,
    organization: {
      name: '',
      logoFile: null,
      logoPreview: '',
      adminEmail: '',
      phone: '',
      address: '',
      description: '',
    },
    templates: {
      contractFile: null,
      billFile: null,
      useDefaults: { contract: true, bill: true },
    },
    buildings: [{ name: '', address: '', apartment_count: 0 }],
    clients: {
      csvFile: null,
      parsedData: [],
      validationErrors: [],
      options: {
        sendWelcomeEmails: true,
        autoGenerateClientNumbers: true,
        createAccountsImmediately: true,
      },
    },
  });

  const updateOrganization = (field: string, value: any) => {
    setState(prev => ({
      ...prev,
      organization: { ...prev.organization, [field]: value }
    }));
  };

  const updateTemplates = (field: string, value: any) => {
    setState(prev => ({
      ...prev,
      templates: { ...prev.templates, [field]: value }
    }));
  };

  const updateBuildings = (buildings: BuildingData[]) => {
    setState(prev => ({ ...prev, buildings }));
  };

  const updateClients = (field: string, value: any) => {
    setState(prev => ({
      ...prev,
      clients: { ...prev.clients, [field]: value }
    }));
  };

  const canProceed = (): boolean => {
    switch (state.currentStep) {
      case 1:
        return !!(state.organization.name && state.organization.adminEmail && state.organization.logoFile);
      case 2:
        return true; // Templates are optional
      case 3:
        const buildingNames = state.buildings.map(b => b.name.toLowerCase().trim()).filter(n => n);
        const hasDuplicates = buildingNames.some((name, i) => buildingNames.indexOf(name) !== i);
        return state.buildings.length > 0 && 
               state.buildings.every(b => b.name && b.apartment_count > 0) &&
               !hasDuplicates;
      case 4:
        return state.clients.parsedData.length > 0 && state.clients.validationErrors.length === 0;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canProceed()) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    } else {
      toast({
        title: "Incomplete Information",
        description: "Please fill in all required fields before proceeding.",
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    setState(prev => ({ ...prev, currentStep: Math.max(1, prev.currentStep - 1) }));
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      const totalClients = state.clients.parsedData.length;
      const estimatedSeconds = Math.ceil(totalClients * 0.5) + 15;
      
      setImportProgress({
        visible: true,
        stage: 'Creating organization...',
        percent: 5,
        estimatedTime: estimatedSeconds
      });

      // Create the organization (requires maxwell.o@asking-group.com to be logged in)
      const { data: newOrganization, error: orgCreateError } = await supabase
        .from('organizations')
        .insert({
          name: state.organization.name,
          admin_email: state.organization.adminEmail,
          status: 'active',
        })
        .select()
        .single();

      if (orgCreateError) throw new Error(`Failed to create organization: ${orgCreateError.message}`);
      const organizationId = newOrganization.id;

      setImportProgress({
        visible: true,
        stage: 'Inviting organization admin...',
        percent: 10,
        estimatedTime: estimatedSeconds
      });

      // Invite the organization admin via Edge Function
      // This will create the auth user, send invitation email, and create the public.users record
      const { data: inviteData, error: inviteError } = await supabase.functions.invoke('create-onboarding-user', {
        body: {
          email: state.organization.adminEmail,
          organizationName: state.organization.name,
          organizationId: organizationId,
          isAdmin: true,
        }
      });

      if (inviteError) throw new Error(`Failed to invite admin: ${inviteError.message}`);
      if (!inviteData?.success) throw new Error(`Failed to invite admin: ${inviteData?.error || 'Unknown error'}`);

      const adminUserId = inviteData.user.id;

      // Edge Function now handles adding the user to organization_admins
      console.log('Admin user created and assigned role:', adminUserId);

      setImportProgress({ 
        visible: true,
        stage: 'Uploading organization logo...', 
        percent: 15, 
        estimatedTime: estimatedSeconds 
      });

      // Upload logo (compulsory)
      let logoUrl = null;
      if (!state.organization.logoFile) {
        throw new Error('Organization logo is required');
      }

      // Validate file size (max 5MB for logos)
      const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
      if (state.organization.logoFile.size > maxSizeInBytes) {
        throw new Error(`Logo file is too large (${(state.organization.logoFile.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 5MB.`);
      }

      const fileExt = state.organization.logoFile.name.split('.').pop();
      const fileName = `${organizationId}-logo-${Date.now()}.${fileExt}`;

      // Upload with retry logic for network errors
      let uploadError = null;
      let retries = 3;

      for (let attempt = 1; attempt <= retries; attempt++) {
        const { error } = await supabase.storage
          .from('organization-logos')
          .upload(fileName, state.organization.logoFile, {
            upsert: true,
            cacheControl: '3600'
          });

        if (!error) {
          uploadError = null;
          break;
        }

        uploadError = error;
        console.error(`Logo upload attempt ${attempt} failed:`, error);

        // If not the last attempt, wait before retrying
        if (attempt < retries) {
          const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff, max 5s
          console.log(`Retrying in ${waitTime}ms...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }

      if (uploadError) {
        console.error('Logo upload failed after retries:', uploadError);
        throw new Error(`Failed to upload logo after ${retries} attempts. Please check your internet connection and try again.`);
      }

      const { data: { publicUrl } } = supabase.storage
        .from('organization-logos')
        .getPublicUrl(fileName);
      
      logoUrl = publicUrl;

      setImportProgress({ 
        visible: true,
        stage: 'Saving organization details...', 
        percent: 25, 
        estimatedTime: estimatedSeconds 
      });

      // Update organization
      const { error: orgError } = await supabase
        .from('organizations')
        .update({
          name: state.organization.name,
          admin_email: state.organization.adminEmail,
          logo_url: logoUrl,
        })
        .eq('id', organizationId);

      if (orgError) throw orgError;

      setImportProgress({ 
        visible: true,
        stage: 'Creating buildings...', 
        percent: 30, 
        estimatedTime: estimatedSeconds 
      });

      // Create buildings
      const buildingsToInsert = state.buildings.map(b => ({
        name: b.name,
        address: b.address,
        apartment_count: b.apartment_count,
        organization_id: organizationId,
      }));

      const { data: createdBuildings, error: buildingsError } = await supabase
        .from('buildings')
        .insert(buildingsToInsert)
        .select();

      if (buildingsError) throw buildingsError;

      // Create apartments for each building
      const apartmentsToInsert: any[] = [];
      createdBuildings.forEach(building => {
        for (let i = 1; i <= building.apartment_count; i++) {
          apartmentsToInsert.push({
            building_id: building.id,
            apartment_number: i.toString(),
            occupancy_status: 'vacant',
            sort_order: i,
          });
        }
      });

      const { data: createdApartments, error: apartmentsError } = await supabase
        .from('apartments')
        .insert(apartmentsToInsert)
        .select();

      if (apartmentsError) throw apartmentsError;

      setImportProgress({ 
        visible: true,
        stage: `Importing clients (0/${totalClients})...`, 
        percent: 40, 
        estimatedTime: estimatedSeconds 
      });

      // Process CSV data: assign meter_ids and create users if option is enabled
      if (state.clients.parsedData.length > 0) {
        let clientCounter = 1;
        const welcomeEmailsToSend: any[] = [];

        for (let i = 0; i < state.clients.parsedData.length; i++) {
          const client = state.clients.parsedData[i];
          
          // Update progress
          const clientProgress = 40 + ((i / totalClients) * 45);
          setImportProgress({ 
            visible: true,
            stage: `Importing clients (${i + 1}/${totalClients})...`, 
            percent: clientProgress, 
            estimatedTime: estimatedSeconds 
          });
          // Find the building
          const building = createdBuildings.find(
            b => b.name.toLowerCase() === client.building_name.toLowerCase()
          );
          if (!building) continue;

          // Find or update the apartment with meter_id
          let apartment = createdApartments.find(
            a => a.building_id === building.id && 
                 a.apartment_number === client.apartment_number
          );
          
          if (apartment) {
            // Update apartment with meter_id
            const { error: updateAptError } = await supabase
              .from('apartments')
              .update({ 
                meter_id: client.meter_id,
                occupancy_status: 'occupied'
              })
              .eq('id', apartment.id);

            if (updateAptError) {
              console.error('Error updating apartment:', updateAptError);
              continue;
            }
          } else {
            // Create new apartment if it doesn't exist
            const { data: newApt, error: createAptError } = await supabase
              .from('apartments')
              .insert({
                building_id: building.id,
                apartment_number: client.apartment_number,
                meter_id: client.meter_id,
                occupancy_status: 'occupied',
              })
              .select()
              .single();

            if (createAptError) {
              console.error('Error creating apartment:', createAptError);
              continue;
            }
            apartment = newApt;
          }

          // Create user if option is enabled
          if (state.clients.options.createAccountsImmediately) {
            // Generate client number
            const clientNumber = state.clients.options.autoGenerateClientNumbers 
              ? `CLI-${organizationId.slice(0, 8).toUpperCase()}-${clientCounter.toString().padStart(4, '0')}`
              : `CLI-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            
            clientCounter++;

            // Only create auth user if email is provided
            if (client.email) {
              // Invite user via Edge Function - this creates auth user, sends email, and creates public.users record
              const { data: inviteResponse, error: inviteError } = await supabase.functions.invoke(
                'create-onboarding-user',
                {
                  body: {
                    email: client.email,
                    organizationName: state.organization.name,
                    organizationId: organizationId,
                    firstName: client.first_name || 'User',
                    lastName: client.last_name || '',
                    clientNumber: clientNumber,
                    apartmentId: apartment.id,
                    isAdmin: false,
                  },
                }
              );

              if (inviteError || !inviteResponse?.success) {
                console.error('Error inviting user:', client.email, inviteError || inviteResponse?.error);
                continue;
              }

              console.log('Successfully invited user:', client.email);
            }
          }
        }

        setImportProgress({ 
          visible: true,
          stage: 'Sending welcome emails...', 
          percent: 90, 
          estimatedTime: estimatedSeconds 
        });

        // Send welcome emails
        if (welcomeEmailsToSend.length > 0) {
          console.log(`Sending ${welcomeEmailsToSend.length} welcome emails...`);
          let emailsSent = 0;
          let emailsFailed = 0;

          for (const emailData of welcomeEmailsToSend) {
            try {
              const { error: emailError } = await supabase.functions.invoke('send-organization-email', {
                body: emailData,
              });

              if (emailError) {
                console.error('Email send error:', emailError);
                emailsFailed++;
              } else {
                emailsSent++;
              }
            } catch (err) {
              console.error('Email send exception:', err);
              emailsFailed++;
            }
          }

          if (emailsSent > 0) {
            toast({
              title: "Welcome Emails",
              description: `${emailsSent} email(s) sent successfully.${emailsFailed > 0 ? ` ${emailsFailed} failed.` : ''}`,
            });
          }
        }
      }

      setImportProgress({ 
        visible: true,
        stage: 'Finalizing setup...', 
        percent: 100, 
        estimatedTime: 0 
      });

      // Brief delay to show completion
      await new Promise(resolve => setTimeout(resolve, 500));

      setImportProgress({ 
        visible: false,
        stage: '', 
        percent: 0, 
        estimatedTime: 0 
      });

      // Clear onboarding flag
      localStorage.removeItem('onboarding_pending');

      toast({
        title: "Setup Complete!",
        description: `Successfully imported ${totalClients} clients and created ${state.buildings.length} buildings.`,
      });

      navigate('/admin/dashboard');
    } catch (error: any) {
      console.error('Onboarding error:', error);
      
      setImportProgress({ 
        visible: false,
        stage: '', 
        percent: 0, 
        estimatedTime: 0 
      });
      
      toast({
        title: "Setup Failed",
        description: error.message || "An error occurred during setup. You will be redirected to try again.",
        variant: "destructive",
      });
      
      // Logout user if authenticated
      try {
        await logout();
      } catch (logoutError) {
        console.error('Logout error:', logoutError);
      }
      
      // Clear any pending onboarding state
      localStorage.removeItem('onboarding_pending');
      
      // Redirect to preauth after a short delay to allow user to see the error
      setTimeout(() => {
        navigate('/preauth', { replace: true });
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, label: 'Organization' },
    { number: 2, label: 'Templates' },
    { number: 3, label: 'Buildings' },
    { number: 4, label: 'Import' },
    { number: 5, label: 'Review' },
  ];

  return (
    <>
      <ImportProgress 
        visible={importProgress.visible}
        stage={importProgress.stage}
        percent={importProgress.percent}
        estimatedTime={importProgress.estimatedTime}
      />
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-5xl">
        <CardContent className="p-6">
          {/* Progress Stepper */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold ${
                      step.number < state.currentStep
                        ? 'bg-primary text-primary-foreground'
                        : step.number === state.currentStep
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step.number < state.currentStep ? <Check className="h-5 w-5" /> : step.number}
                  </div>
                  <span className="text-xs mt-2 text-muted-foreground">{step.label}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      step.number < state.currentStep ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step Content */}
          <div className="min-h-[500px]">
            {state.currentStep === 1 && (
              <Step1Organization organizationData={state.organization} onChange={updateOrganization} />
            )}
            {state.currentStep === 2 && (
              <Step2Templates templatesData={state.templates} onChange={updateTemplates} />
            )}
            {state.currentStep === 3 && (
              <Step3Buildings buildings={state.buildings} onChange={updateBuildings} />
            )}
            {state.currentStep === 4 && (
              <Step4CSVImport
                clientsData={state.clients}
                buildings={state.buildings}
                onChange={updateClients}
              />
            )}
            {state.currentStep === 5 && (
              <Step5Review
                organizationData={state.organization}
                templatesData={state.templates}
                buildings={state.buildings}
                clientsData={state.clients}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={state.currentStep === 1 || loading}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <div className="text-sm text-muted-foreground">
              Step {state.currentStep} of {steps.length}
            </div>

            {state.currentStep < 5 ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={!canProceed() || loading}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={completeOnboarding}
                disabled={loading}
              >
                {loading ? 'Setting up...' : 'Complete Setup & Import'}
                <Check className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      </div>
    </>
  );
};

export default OnboardingSlides;
